/**
 * Handing a photo back to the phone it was taken on.
 *
 * A photo taken through a file input with `capture` never reaches the camera
 * roll: the OS hands the frame straight to the page and keeps no copy. Nothing
 * on the web can write to Photos either, so the only route back is the system
 * share sheet, where the person taps "Save Image" themselves.
 *
 * Two consequences shape everything below. The sheet only opens from a genuine
 * tap, so callers must invoke this from a click handler and not from the file
 * input's own change event. And it is not available everywhere — a desktop
 * browser shares links but not files — so callers ask first and offer nothing
 * when the answer is no.
 */

type ShareOutcome = 'shared' | 'dismissed' | 'failed';

/** The slice of the Web Share API we use; older TS DOM libs omit `canShare`. */
type FileSharingNavigator = Navigator & {
  canShare?: (data: { files: File[] }) => boolean;
  share?: (data: { files: File[] }) => Promise<void>;
};

/**
 * Whether offering to save these photos would lead anywhere. False on desktop,
 * false where the API is missing, and false with nothing to share — an offer
 * that cannot be honoured is worse than no offer.
 */
export function canSharePhotos(files: File[]): boolean {
  if (files.length === 0) return false;

  const nav = navigator as FileSharingNavigator;
  if (typeof nav.share !== 'function' || typeof nav.canShare !== 'function') return false;

  try {
    return nav.canShare({ files });
  } catch {
    // A browser that cannot answer the question cannot serve the request.
    return false;
  }
}

/**
 * Opens the share sheet on the whole batch, so a burst of shots costs one sheet
 * rather than one per photo.
 *
 * Closing the sheet rejects with AbortError, which is a decision rather than a
 * fault and is reported separately: the caller should say nothing about it.
 */
export async function sharePhotos(files: File[]): Promise<ShareOutcome> {
  if (!canSharePhotos(files)) return 'failed';

  try {
    await (navigator as FileSharingNavigator).share!({ files });
    return 'shared';
  } catch (error) {
    // Name rather than instanceof: what a browser throws here is a DOMException
    // in the spec and not always one in practice.
    if ((error as { name?: string } | null)?.name === 'AbortError') return 'dismissed';
    return 'failed';
  }
}
